import { useState, useEffect } from 'react';
import api from '@/api';
import { Loader2, Wrench, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';
import FilterBar, { FilterSelect, FilterInput, FilterDate } from '@/components/FilterBar';
import { STATUS_COLORS, REPAIR_STATUSES } from '@/constants/statusColors';
import useRepairCategories from '@/hooks/useRepairCategories';

const STATUSES = REPAIR_STATUSES.filter(s => s !== 'cancelled');
const EMPTY = { status:'', category:'', unit_search:'', vendor:'', payment_state:'', date_from:'', date_to:'' };

export default function PortalRepairs() {
  const { names: CATS } = useRepairCategories();
  const [repairs, setRepairs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY);

  const activeCount = Object.values(filters).filter(v => v !== '').length;

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''));
      params.limit = 50;
      const res = await api.get('/repairs', { params });
      setRepairs(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load repairs');
    } finally {
      setLoading(false);
    }
  };

  const setF = (key, val) => setFilters(p => ({ ...p, [key]: val }));

  return (
    <div className="space-y-5 animate-fade-in" data-testid="portal-repairs-page">
      <div>
        <h1 className="font-['Manrope'] font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>My Repairs</h1>
        <p className="font-['IBM_Plex_Sans'] text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{total} repair events</p>
      </div>

      <FilterBar onClear={() => setFilters(EMPTY)} activeCount={activeCount}>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
          <input value={filters.unit_search} onChange={e => setF('unit_search', e.target.value)}
            placeholder="Search unit #, VIN, plate, repair ID..."
            className="w-full input-slate pl-8" />
        </div>
        <div className="flex flex-wrap gap-3">
          <FilterSelect label="Status" value={filters.status} onChange={v => setF('status', v)}
            options={STATUSES.map(s => ({ value:s, label:s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) }))} />
          <FilterSelect label="Category" value={filters.category} onChange={v => setF('category', v)}
            options={CATS.map(c => ({ value:c, label:c }))} />
          <FilterSelect label="Payment" value={filters.payment_state} onChange={v => setF('payment_state', v)}
            options={[{value:'unpaid',label:'Unpaid'},{value:'paid',label:'Paid'},{value:'overdue',label:'Overdue'}]} />
          <FilterInput label="Vendor" value={filters.vendor} onChange={v => setF('vendor', v)} placeholder="Shop name..." />
          <FilterDate label="From" value={filters.date_from} onChange={v => setF('date_from', v)} />
          <FilterDate label="To" value={filters.date_to} onChange={v => setF('date_to', v)} />
        </div>
      </FilterBar>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
      ) : repairs.length === 0 ? (
        <div className="py-16 text-center rounded-md border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <Wrench size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
          <p className="font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>No repair events match your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {repairs.map(r => (
            <div key={r.id} className="p-5 rounded-md border transition-colors"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              data-testid={`portal-repair-card-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  {r.priority === 'emergency' && <AlertTriangle size={16} className="flex-shrink-0" style={{ color: 'var(--error)' }} />}
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-['JetBrains_Mono'] text-sm font-bold" style={{ color: 'var(--primary)' }}>{r.truck_unit_number||'N/A'}</span>
                      <span className="text-[10px] uppercase tracking-wider font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>{r.category}</span>
                    </div>
                    <p className="text-sm font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-primary)' }}>{r.issue_description}</p>
                    <p className="text-xs font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-muted)' }}>{r.location}{r.vendor_name?` · ${r.vendor_name}`:''}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['IBM_Plex_Sans'] ${STATUS_COLORS[r.status]||STATUS_COLORS.open}`}>
                    {r.status?.replace(/_/g,' ')}
                  </span>
                  {r.estimate_amount && <span className="font-['JetBrains_Mono'] text-sm" style={{ color: 'var(--text-primary)' }}>${r.estimate_amount.toLocaleString()}</span>}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                <span className={`text-[10px] font-bold uppercase font-['IBM_Plex_Sans'] ${r.priority==='emergency'?'text-[var(--error)]':r.priority==='high'?'text-orange-400':r.priority==='medium'?'text-[var(--primary)]':''}`} style={{ color: r.priority === 'low' ? 'var(--text-muted)' : undefined }}>{r.priority} priority</span>
                {r.downtime_hours && <span className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>{r.downtime_hours}h downtime</span>}
                {r.invoice_pdf_url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${r.invoice_pdf_url}?token=${localStorage.getItem('mm_token')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold font-['Manrope'] uppercase tracking-wide transition-colors"
                    style={{ background: 'var(--success-muted)', border: '1px solid var(--success)', color: 'var(--success)' }}
                    onClick={e => e.stopPropagation()}>
                    ↓ Invoice PDF
                  </a>
                )}
                <span className="text-xs font-['IBM_Plex_Sans'] ml-auto" style={{ color: 'var(--text-faint)' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
